import { useMemo } from 'react';
import { Modal, Tag, Space, Typography, Progress, Alert, Button, Tooltip } from 'antd';
import {
  ScissorOutlined, FormatPainterOutlined, ToolOutlined, ExperimentOutlined,
  FireOutlined, InboxOutlined, CarOutlined, LockOutlined, ArrowRightOutlined,
  CheckCircleFilled, ClockCircleOutlined, MinusOutlined,
  BuildOutlined, BorderOutlined, HighlightOutlined, SafetyOutlined, AppstoreOutlined
} from '@ant-design/icons';
import {
  woStatusLabel, woStatusColor, progressPercent
} from './workOrderHelpers';

// ════════════════════════════════════════════════════════════════════
// Stage WO-1A — Work Order "Production Stages" modal.
//
// Mirrors the shop's legacy per-order updation popup (open an order → see its
// department stages), but rendered in the Glass-i AntD theme (brand blue +
// semantic status colours, not the legacy saturated tiles). Read-only: each
// tile shows the department's ROUTED piece workload and a status derived from
// the work order's overall state. The click-to-advance write and "Close This
// Order" belong to the WO-1B scan engine (paused) — surfaced here, disabled,
// so the design + data organization are in place without a backend change.
// ════════════════════════════════════════════════════════════════════

const { Text } = Typography;

// Department code → icon (Glass-i mapping of the well-known main stages).
// Any department not listed (e.g. a newly-added "TEST") falls back to the
// generic AppstoreOutlined, so the tiles stay dynamic/server-driven.
const DEPT_ICON = {
  CUTTING:        <ScissorOutlined />,
  POLISHING:      <FormatPainterOutlined />,
  FABRICATION:    <ToolOutlined />,
  WASHING:        <ExperimentOutlined />,
  TEMPERING:      <FireOutlined />,
  LAMINATION:     <BuildOutlined />,
  DGU:            <BorderOutlined />,
  DESIGN:         <HighlightOutlined />,
  QC:             <SafetyOutlined />,
  READY_DISPATCH: <InboxOutlined />,
  PACKING:        <InboxOutlined />,
  DISPATCH:       <CarOutlined />
};
const DEFAULT_DEPT_ICON = <AppstoreOutlined />;

// Tile visual state from the REAL per-department completed/routed piece counts
// (from the production scan log): done when every routed piece cleared the
// stage, active while some have, pending when none have, off when not in route.
function tileState(done, routed) {
  if (!routed) return { cls: 'off', label: 'Not in route', badge: <MinusOutlined /> };
  if (done >= routed) return { cls: 'done', label: 'Done', badge: <CheckCircleFilled /> };
  if (done > 0) return { cls: 'active', label: 'In progress', badge: <ClockCircleOutlined /> };
  return { cls: 'pending', label: 'Pending', badge: <ClockCircleOutlined /> };
}

export default function WorkOrderStagesModal({ open, row, stageColumns, onClose, onOpenDetail }) {
  const r = row || {};
  const cols = Array.isArray(stageColumns) ? stageColumns : [];
  const routedTotal = useMemo(
    () => cols.reduce((s, c) => s + (Number(r.stages?.[c.code]?.routed) || 0), 0),
    [row, cols] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const completed = Number(r.completedCount) || 0;
  const total = Number(r.pieceCount) || 0;
  const pct = progressPercent(completed, total);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={620}
      className="erp-wo-stages-modal"
      title={
        <Space size="middle" align="center" wrap>
          <span className="erp-wo-stages-title">Production Stages</span>
          {r.woNo ? <code className="erp-grid__code">{r.woNo}</code> : null}
          {r.proformaNo ? <Text type="secondary" style={{ fontSize: 12 }}>PI {r.proformaNo}</Text> : null}
          {r.status ? <Tag color={woStatusColor(r.status)} style={{ marginInlineEnd: 0 }}>{(woStatusLabel(r.status) || '').toUpperCase()}</Tag> : null}
        </Space>
      }
      footer={
        <div className="erp-wo-stages-footer">
          <Tooltip title="Marking stages complete and closing the order arrive with the production scan engine (WO-1B).">
            <Button danger disabled icon={<LockOutlined />}>Close This Order</Button>
          </Tooltip>
          <Space>
            <Button onClick={onClose}>Close</Button>
            <Button type="primary" icon={<ArrowRightOutlined />}
              onClick={() => { if (onOpenDetail) onOpenDetail(r); }}>
              Open work order
            </Button>
          </Space>
        </div>
      }
    >
      {/* Customer + overall progress */}
      <div className="erp-wo-stages-head">
        <div className="erp-wo-stages-cust">
          <Text type="secondary" style={{ fontSize: 11 }}>CUSTOMER</Text>
          <div className="erp-wo-stages-cust-name">{r.customerName || '—'}</div>
        </div>
        <div className="erp-wo-stages-prog">
          <div className="erp-wo-stages-prog-row">
            <Text type="secondary" style={{ fontSize: 11 }}>OVERALL</Text>
            <span className="tabular-nums erp-wo-stages-prog-count">{completed}/{total} pcs</span>
          </div>
          <Progress percent={pct} size="small" showInfo={false}
            strokeColor={pct === 100 ? '#16a34a' : '#1877F2'} />
        </div>
      </div>

      {/* Department stage tiles */}
      <div className="erp-wo-stages-grid">
        {cols.map((c) => {
          const cell = r.stages?.[c.code] || { done: 0, routed: 0 };
          const routed = Number(cell.routed) || 0;
          const done = Number(cell.done) || 0;
          const st = tileState(done, routed);
          return (
            <div key={c.code} className={`erp-wo-stage-tile erp-wo-stage-tile--${st.cls}`}>
              <div className="erp-wo-stage-tile__badge">{st.badge}</div>
              <div className="erp-wo-stage-tile__icon">{DEPT_ICON[c.code] || DEFAULT_DEPT_ICON}</div>
              <div className="erp-wo-stage-tile__name">{c.name}</div>
              <div className="erp-wo-stage-tile__meta">
                {routed ? <span className="tabular-nums">{done}/{routed} pcs · {st.label}</span> : <span>{st.label}</span>}
              </div>
            </div>
          );
        })}
      </div>

      <Alert type="info" showIcon className="mt-3"
        message="Completed / routed pieces per department"
        description="Each tile shows how many routed pieces have cleared that department, from the production scan log. Tap-to-advance and Close This Order arrive with the scan station."
      />
    </Modal>
  );
}
